import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { WORKSPACE_URL } from "../../constants/links";

const NAV = [
  { label: "Product", href: "#why" },
  { label: "How it works", href: "#how" },
  { label: "Builder", href: "#builder" },
  { label: "Workspace", href: "#workspace" },
  { label: "FAQ", href: "#faq" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "py-2" : "py-3"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div
          className={`flex items-center justify-between rounded-2xl border border-black/[0.06] glass-strong shadow-soft transition-all duration-300 ${
            scrolled ? "px-3 py-2" : "px-4 py-2.5"
          }`}
        >
          {/* Brand */}
          <a href="#top" className="flex items-center gap-2.5" data-testid="brand-link">
            <div className="flex flex-col leading-none">
              <span className="font-display font-bold text-[15px] tracking-tight">
                DeMaxtore
              </span>
              <span className="font-mono text-[10px] tracking-[0.22em] text-black/50 mt-1">
                WORKSPACE
              </span>
            </div>
            <span className="ml-2 hidden sm:inline-flex items-center gap-1 rounded-full bg-[#0044FF]/8 text-[#0044FF] font-mono text-[10px] tracking-[0.18em] px-2 py-1 border border-[#0044FF]/15">
              RFQ™
            </span>
          </a>

          {/* Center nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-[13px] font-medium text-black/70 hover:text-black px-3 py-2 rounded-lg hover:bg-black/[0.04] transition-colors"
              >
                {n.label}
              </a>
            ))}
          </nav>

          {/* Right CTAs */}
          <div className="flex items-center gap-2">
            <a
              data-testid="header-create-rfq-btn"
              href={WORKSPACE_URL}
              className="hidden sm:inline-flex items-center gap-1.5 btn-primary text-[13px] py-2.5 px-3.5"
            >
              Create Your RFQ™
              <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.25} />
            </a>
            <button
              data-testid="header-mobile-menu-btn"
              className="lg:hidden btn-ghost p-2"
              onClick={() => setMenuOpen((s) => !s)}
              aria-label="Toggle menu"
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden mt-2 rounded-2xl border border-black/[0.06] glass-strong p-3 shadow-card"
              data-testid="mobile-menu"
            >
              <div className="grid">
                {NAV.map((n) => (
                  <a
                    key={n.href}
                    href={n.href}
                    onClick={() => setMenuOpen(false)}
                    className="px-3 py-2.5 rounded-lg text-[14px] font-medium text-black/80 hover:bg-black/[0.04]"
                  >
                    {n.label}
                  </a>
                ))}
                <a
                  data-testid="mobile-create-rfq-btn"
                  href={WORKSPACE_URL}
                  onClick={() => setMenuOpen(false)}
                  className="mt-2 btn-primary w-full text-[14px] text-center"
                >
                  Create Your RFQ™
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}
